<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import { useCurrency } from 'dashboard/composables/useCurrency';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import KanbanAPI from 'dashboard/api/kanban';
import Spinner from 'shared/components/Spinner.vue';
import WinDealModal from './WinDealModal.vue';
import LoseDealModal from './LoseDealModal.vue';
import { getDealTotalValue } from './dealValue';

// Ao resolver uma conversa com negócio(s) ABERTO(s) no funil (CFM), pergunta o
// que fazer com cada um: Ganho / Perdido / Mover / Manter em aberto. Reusa os
// endpoints do Kanban (mark_deal_won/lost, transfer_to_funnel).
const props = defineProps({
  show: { type: Boolean, default: false },
  conversation: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['close']);

const { t, locale } = useI18n();
const store = useStore();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { funnels, loadFunnels } = useFunnelManager();
const { currency } = useCurrency();

const loading = ref(false);
const mappings = ref([]);
const dontAskAgain = ref(false);

// Sub-modais / mover
const activeMapping = ref(null);
const showWin = ref(false);
const showLose = ref(false);
const movingId = ref(null);
const moveFunnelId = ref(null);
const moveStage = ref(null);
const activeDealValue = computed(() =>
  getDealTotalValue(props.conversation, {
    mapping: activeMapping.value,
    funnelId: activeMapping.value?.funnel_id,
  })
);

const conversationId = computed(() => props.conversation?.id);
const contactName = computed(
  () =>
    props.conversation?.meta?.sender?.name ||
    props.conversation?.contact?.name ||
    t('KANBAN.DEAL.RESOLVE_PROMPT.FALLBACK_CONTACT')
);

const skipFlag = computed(
  () => props.conversation?.custom_attributes?.skip_deal_resolve_prompt === true
);

const formatCurrency = amount =>
  new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
  }).format(parseFloat(amount) || 0);

const closeSelf = async () => {
  if (dontAskAgain.value && conversationId.value) {
    await store.dispatch('updateCustomAttributes', {
      conversationId: conversationId.value,
      customAttributes: {
        ...(props.conversation?.custom_attributes || {}),
        skip_deal_resolve_prompt: true,
      },
    });
  }
  emit('close');
};

const fetchOpenMappings = async () => {
  loading.value = true;
  try {
    const res = await ConversationFunnelMappingsAPI.getMappings(
      conversationId.value
    );
    mappings.value = (res.data || []).filter(m => m.deal_status === 'open');
  } catch {
    mappings.value = [];
  } finally {
    loading.value = false;
  }
  // Nada aberto → fecha sem incomodar.
  if (mappings.value.length === 0) emit('close');
};

// Refaz a lista após uma ação; se zerar, fecha.
const afterAction = async () => {
  await fetchOpenMappings();
  if (mappings.value.length === 0) emit('close');
};

// --- Ganho ---
const openWin = mapping => {
  activeMapping.value = mapping;
  showWin.value = true;
};
const isMarkingDealWon = ref(false);
const confirmWin = async data => {
  isMarkingDealWon.value = true;
  try {
    await KanbanAPI.markDealAsWon(
      activeMapping.value.funnel_id,
      conversationId.value,
      {
        final_value: data.finalValue,
        close_date: data.closeDate,
        notes: data.notes,
        won_reason: data.wonReason,
        won_reason_id: data.wonReasonId,
      }
    );
    useAlert(t('KANBAN.DEAL.WIN.SUCCESS'));
  } catch {
    useAlert(t('KANBAN.DEAL.WIN.ERROR'));
  } finally {
    isMarkingDealWon.value = false;
    showWin.value = false;
    await afterAction();
  }
};

// --- Perdido ---
const openLose = mapping => {
  activeMapping.value = mapping;
  showLose.value = true;
};
const confirmLose = async data => {
  try {
    await KanbanAPI.markDealAsLost(
      activeMapping.value.funnel_id,
      conversationId.value,
      {
        reason: data.reason,
        reason_id: data.reasonId,
        notes: data.notes,
      }
    );
    useAlert(t('KANBAN.DEAL.LOSE.SUCCESS'));
  } catch {
    useAlert(t('KANBAN.DEAL.LOSE.ERROR'));
  } finally {
    showLose.value = false;
    await afterAction();
  }
};

// --- Excluir (remove o negócio do funil) ---
const confirmDelete = async mapping => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.DEAL.RESOLVE_PROMPT.DELETE_CONFIRM'))) return;
  try {
    await ConversationFunnelMappingsAPI.removeFromFunnel(
      conversationId.value,
      mapping.id
    );
    useAlert(t('KANBAN.DEAL.RESOLVE_PROMPT.DELETE_SUCCESS'));
  } catch {
    useAlert(t('KANBAN.DEAL.RESOLVE_PROMPT.DELETE_ERROR'));
  } finally {
    await afterAction();
  }
};

// --- Mover (seletor inline funil + etapa) ---
const movingFunnelId = computed(
  () => mappings.value.find(m => m.id === movingId.value)?.funnel_id
);
const targetFunnels = computed(() =>
  funnels.value.filter(f => f.active && f.id !== movingFunnelId.value)
);
const targetStages = computed(() => {
  const f = funnels.value.find(fn => fn.id === moveFunnelId.value);
  if (!f?.stages) return [];
  return Object.entries(f.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([name, cfg]) => ({ name, label: cfg.label || name }));
});
const startMove = mapping => {
  movingId.value = movingId.value === mapping.id ? null : mapping.id;
  moveFunnelId.value = null;
  moveStage.value = null;
};
const confirmMove = async mapping => {
  if (!moveFunnelId.value || !moveStage.value) return;
  try {
    await KanbanAPI.transferToFunnel(
      mapping.funnel_id,
      conversationId.value,
      moveFunnelId.value,
      moveStage.value,
      true
    );
    useAlert(t('KANBAN.TRANSFER.SUCCESS', { funnelName: '' }));
  } catch {
    useAlert(t('KANBAN.TRANSFER.ERROR'));
  } finally {
    movingId.value = null;
    await afterAction();
  }
};

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) return;
    if (skipFlag.value) {
      emit('close');
      return;
    }
    dontAskAgain.value = false;
    movingId.value = null;
    loadFunnels();
    fetchOpenMappings();
  }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show && !skipFlag"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      @click.self="closeSelf"
    >
      <div
        class="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-5">
          <div class="flex items-center gap-2.5">
            <span
              class="flex h-9 w-9 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
            >
              <span class="i-lucide-briefcase text-lg" />
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('KANBAN.DEAL.RESOLVE_PROMPT.TITLE') }}
              </h3>
              <p class="truncate text-sm text-n-slate-10">{{ contactName }}</p>
            </div>
          </div>
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="closeSelf"
          >
            <span class="i-lucide-x text-base" />
          </button>
        </div>

        <div v-if="loading" class="flex justify-center px-5 pb-6">
          <Spinner />
        </div>

        <!-- Lista de negócios abertos -->
        <div v-else class="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 pb-2">
          <div
            v-for="mapping in mappings"
            :key="mapping.id"
            class="rounded-xl border border-n-weak bg-n-solid-1 p-3"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-n-slate-12">
                  {{ mapping.funnel_name }}
                </p>
                <p
                  class="mt-0.5 flex items-center gap-1.5 text-xs text-n-slate-11"
                >
                  <span
                    v-if="mapping.stage_color"
                    class="h-2 w-2 shrink-0 rounded-full"
                    :style="{ backgroundColor: mapping.stage_color }"
                  />
                  {{ mapping.stage_label || mapping.stage_name }}
                </p>
              </div>
              <span
                v-if="mapping.deal_value"
                class="shrink-0 text-sm font-bold tabular-nums text-n-teal-11"
              >
                {{ formatCurrency(mapping.deal_value) }}
              </span>
            </div>

            <!-- Ações -->
            <div class="mt-3 flex flex-wrap gap-1.5">
              <button
                type="button"
                class="flex items-center gap-1 rounded-md bg-n-teal-3 px-2.5 py-1.5 text-xs font-medium text-n-teal-11 transition-colors hover:bg-n-teal-4"
                @click="openWin(mapping)"
              >
                <span class="i-lucide-trophy text-sm" />
                {{ t('KANBAN.DEAL.RESOLVE_PROMPT.WON') }}
              </button>
              <button
                type="button"
                class="flex items-center gap-1 rounded-md bg-n-ruby-3 px-2.5 py-1.5 text-xs font-medium text-n-ruby-11 transition-colors hover:bg-n-ruby-4"
                @click="openLose(mapping)"
              >
                <span class="i-lucide-circle-x text-sm" />
                {{ t('KANBAN.DEAL.RESOLVE_PROMPT.LOST') }}
              </button>
              <button
                type="button"
                class="flex items-center gap-1 rounded-md bg-n-alpha-2 px-2.5 py-1.5 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-3"
                @click="startMove(mapping)"
              >
                <span class="i-lucide-arrow-left-right text-sm" />
                {{ t('KANBAN.DEAL.RESOLVE_PROMPT.MOVE') }}
              </button>
              <button
                type="button"
                class="flex items-center gap-1 rounded-md bg-n-alpha-2 px-2.5 py-1.5 text-xs font-medium text-n-ruby-11 transition-colors hover:bg-n-ruby-3"
                @click="confirmDelete(mapping)"
              >
                <span class="i-lucide-trash-2 text-sm" />
                {{ t('KANBAN.DEAL.RESOLVE_PROMPT.DELETE') }}
              </button>
            </div>

            <!-- Mover: seletor inline -->
            <div
              v-if="movingId === mapping.id"
              class="mt-3 space-y-2 border-t border-n-weak pt-3"
            >
              <select
                v-model="moveFunnelId"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              >
                <option :value="null" disabled>
                  {{ t('KANBAN.TRANSFER.CHOOSE_FUNNEL') }}
                </option>
                <option v-for="f in targetFunnels" :key="f.id" :value="f.id">
                  {{ f.name }}
                </option>
              </select>
              <select
                v-if="moveFunnelId"
                v-model="moveStage"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              >
                <option :value="null" disabled>
                  {{ t('KANBAN.TRANSFER.CHOOSE_STAGE') }}
                </option>
                <option v-for="s in targetStages" :key="s.name" :value="s.name">
                  {{ s.label }}
                </option>
              </select>
              <button
                type="button"
                class="w-full rounded-lg bg-n-brand py-2 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-40"
                :disabled="!moveFunnelId || !moveStage"
                @click="confirmMove(mapping)"
              >
                {{ t('KANBAN.DEAL.RESOLVE_PROMPT.MOVE_CONFIRM') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="space-y-3 p-5">
          <label class="flex cursor-pointer items-center gap-2">
            <input
              v-model="dontAskAgain"
              type="checkbox"
              class="h-4 w-4 accent-n-brand"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('KANBAN.DEAL.RESOLVE_PROMPT.DONT_ASK') }}
            </span>
          </label>
          <button
            type="button"
            class="w-full rounded-lg border border-n-weak py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2"
            @click="closeSelf"
          >
            {{ t('KANBAN.DEAL.RESOLVE_PROMPT.KEEP_OPEN') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sub-modais reaproveitados -->
    <WinDealModal
      :show="showWin"
      :conversation="conversation"
      :current-value="activeDealValue"
      :is-submitting="isMarkingDealWon"
      @close="showWin = false"
      @confirm="confirmWin"
    />
    <LoseDealModal
      :show="showLose"
      :conversation="conversation"
      @close="showLose = false"
      @confirm="confirmLose"
    />
  </Teleport>
</template>
